import { useState, useEffect, useMemo, useCallback } from 'react';
import { Patient, TestResult } from '../types';
import { getPatients } from '../services/patientService';
import { getTestResults } from '../services/testResultService';
import { User } from '@supabase/supabase-js';

interface UseAppDataResult {
  patients: Patient[];
  results: TestResult[];
  patientsMap: Map<string, Patient>;
  loading: boolean;
  loadingPatients: boolean;
  loadingResults: boolean;
  refreshPatients: () => Promise<void>;
  refreshResults: () => Promise<void>;
  refreshAll: () => Promise<void>;
  setResults: React.Dispatch<React.SetStateAction<TestResult[]>>;
}

/**
 * Hook that consolidates loading of patients and test results
 * Solves the N+1 query problem by enriching results with patient names from a map
 */
export function useAppData(user: User | null): UseAppDataResult {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [results, setResults] = useState<TestResult[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingResults, setLoadingResults] = useState(true);

  // Create a map of patients by ID for quick lookup (solves N+1 problem)
  const patientsMap = useMemo(() => {
    const map = new Map<string, Patient>();
    patients.forEach(p => map.set(p.id, p));
    return map;
  }, [patients]);

  // Load patients
  const loadPatients = useCallback(async () => {
    if (!user) {
      setPatients([]);
      setLoadingPatients(false);
      return;
    }

    setLoadingPatients(true);
    try {
      const patientsData = await getPatients(true); // include archived
      setPatients(patientsData);
    } catch (error) {
      console.error('Error loading patients:', error);
      setPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  }, [user?.id]);

  // Load results and enrich with patient names using the map
  const loadResults = useCallback(async (patientMap?: Map<string, Patient>) => {
    if (!user) {
      setResults([]);
      setLoadingResults(false);
      return;
    }

    setLoadingResults(true);
    try {
      const supabaseResults = await getTestResults();

      // Use provided map or current patientsMap
      const mapToUse = patientMap || patientsMap;

      // Enrich results with patient names using the map (O(1) lookup instead of N queries)
      const enrichedResults = supabaseResults.map(result => {
        if (result.patientId && !result.patientName) {
          const patient = mapToUse.get(result.patientId);
          return { ...result, patientName: patient?.name || 'Paciente Desconocido' };
        }
        return result;
      });

      setResults(enrichedResults);
    } catch (error) {
      console.error('Error loading results:', error);
      setResults([]);
    } finally {
      setLoadingResults(false);
    }
  }, [user?.id, patientsMap]);

  // Load all data in parallel on mount or when user changes
  useEffect(() => {
    if (!user) {
      setPatients([]);
      setResults([]);
      setLoadingPatients(false);
      setLoadingResults(false);
      return;
    }

    const loadAllData = async () => {
      setLoadingPatients(true);
      setLoadingResults(true);

      try {
        // Load patients first, then results (results need patient names)
        const patientsData = await getPatients(true);
        setPatients(patientsData);
        setLoadingPatients(false);

        // Create map for enriching results
        const patientMap = new Map<string, Patient>();
        patientsData.forEach(p => patientMap.set(p.id, p));

        // Now load results with the patient map
        const supabaseResults = await getTestResults();
        const enrichedResults = supabaseResults.map(result => {
          if (result.patientId && !result.patientName) {
            const patient = patientMap.get(result.patientId);
            return { ...result, patientName: patient?.name || 'Paciente Desconocido' };
          }
          return result;
        });

        setResults(enrichedResults);
      } catch (error) {
        console.error('Error loading app data:', error);
      } finally {
        setLoadingPatients(false);
        setLoadingResults(false);
      }
    };

    loadAllData();
  }, [user?.id]);

  // Refresh functions for manual updates
  const refreshPatients = useCallback(async () => {
    await loadPatients();
  }, [loadPatients]);

  const refreshResults = useCallback(async () => {
    await loadResults();
  }, [loadResults]);

  const refreshAll = useCallback(async () => {
    if (!user) return;

    setLoadingPatients(true);
    setLoadingResults(true);

    try {
      const patientsData = await getPatients(true);
      setPatients(patientsData);

      const patientMap = new Map<string, Patient>();
      patientsData.forEach(p => patientMap.set(p.id, p));

      await loadResults(patientMap);
    } finally {
      setLoadingPatients(false);
      setLoadingResults(false);
    }
  }, [user?.id, loadResults]);

  const loading = loadingPatients || loadingResults;

  return {
    patients,
    results,
    patientsMap,
    loading,
    loadingPatients,
    loadingResults,
    refreshPatients,
    refreshResults,
    refreshAll,
    setResults,
  };
}
